import { OrbitCamera } from './camera';
import { Renderer } from './renderer';
import { TileManager } from './tile-manager';
import { HIRES_RES, MAX_LOD1_TILES, DETAIL_SCALE, DETAIL_ZOOM_OPTIONS, INFER_BUDGET, setDetailZoom, type GlobalManifest } from './types';

async function main(): Promise<void> {
  if (!navigator.gpu) {
    showError('WebGPU is not supported in this browser. Use Chrome 113+ or Edge 113+.');
    return;
  }

  const canvas = document.getElementById('canvas') as HTMLCanvasElement;
  const hud = document.getElementById('hud')!;

  // Size canvas to window
  const resize = () => {
    canvas.width = window.innerWidth * devicePixelRatio;
    canvas.height = window.innerHeight * devicePixelRatio;
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;
  };
  resize();

  // Show loading
  hud.textContent = 'Loading...';

  // Fetch manifest + LOD0 blob in parallel
  const [manifest, lod0Blob] = await Promise.all([
    fetch('data/manifest.json').then(r => r.json()) as Promise<GlobalManifest>,
    fetch('data/lod0_tiles.bin').then(r => r.arrayBuffer()),
  ]);

  hud.textContent = `Loaded ${manifest.slots} tiles, initializing WebGPU...`;

  // Init renderer
  const renderer = new Renderer();
  await renderer.init(canvas);

  // Parse URL params
  const urlParams = new URLSearchParams(window.location.search);
  const gpuCbtEnabled = urlParams.has('gpu-cbt');

  // Init tile manager
  const tileManager = await TileManager.create(manifest, lod0Blob, renderer, 'data/weights/', gpuCbtEnabled);

  // Detail zoom dropdown (effective resolution = 1024 × zoom)
  const resSelect = document.getElementById('res-select') as HTMLSelectElement;
  for (const zoom of DETAIL_ZOOM_OPTIONS) {
    const opt = document.createElement('option');
    opt.value = String(zoom);
    const effRes = 1024 * zoom;
    opt.textContent = `${effRes}`;
    if (zoom === 4) opt.selected = true; // default 4× = 4096 effective
    resSelect.appendChild(opt);
  }
  resSelect.addEventListener('change', () => {
    const zoom = parseInt(resSelect.value, 10);
    tileManager.setDetailZoom(zoom);
  });

  // Camera — center on the globe grid
  const camera = new OrbitCamera();
  camera.target = {
    x: manifest.n_lon / 2 - 0.5,
    y: 0,
    z: manifest.n_lat / 2 - 0.5,
  };
  camera.distance = 20;
  camera.elevation = 0.8;

  // --- Input ---
  let mouseDown = false;
  let rightDown = false;
  const keys = new Set<string>();

  canvas.addEventListener('mousedown', (e) => {
    if (e.button === 0) mouseDown = true;
    if (e.button === 2) rightDown = true;
  });
  window.addEventListener('mouseup', (e) => {
    if (e.button === 0) mouseDown = false;
    if (e.button === 2) rightDown = false;
  });
  canvas.addEventListener('mousemove', (e) => {
    if (mouseDown) camera.rotate(e.movementX, e.movementY);
  });
  canvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    camera.zoom(e.deltaY > 0 ? -1 : 1);
  }, { passive: false });
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());

  window.addEventListener('keydown', (e) => keys.add(e.code));
  window.addEventListener('keyup', (e) => keys.delete(e.code));

  window.addEventListener('resize', () => {
    resize();
    renderer.resize(canvas.width, canvas.height);
  });

  // --- State ---
  let elevScale = 1.0;
  let lodThreshold = 4.0;
  let lastTime = performance.now();
  let frameCount = 0;
  let fps = 0;
  let lastFpsTime = lastTime;

  // --- rAF loop ---
  function frame(): void {
    const now = performance.now();
    const dt = (now - lastTime) / 1000;
    lastTime = now;

    // FPS counter
    frameCount++;
    if (now - lastFpsTime > 1000) {
      fps = frameCount / ((now - lastFpsTime) / 1000);
      frameCount = 0;
      lastFpsTime = now;
    }

    // Keyboard
    const panSpeed = 1;
    if (keys.has('KeyW')) camera.pan(0, panSpeed);
    if (keys.has('KeyS')) camera.pan(0, -panSpeed);
    if (keys.has('KeyA')) camera.pan(panSpeed, 0);
    if (keys.has('KeyD')) camera.pan(-panSpeed, 0);

    // Elevation scale
    if (keys.has('ArrowUp') || keys.has('Equal') || keys.has('NumpadAdd')) elevScale *= 1.02;
    if (keys.has('ArrowDown') || keys.has('Minus') || keys.has('NumpadSubtract')) elevScale *= 0.98;

    // LOD threshold
    if (keys.has('KeyL')) lodThreshold = Math.min(16, lodThreshold * 1.05);
    if (keys.has('KeyK')) lodThreshold = Math.max(0.5, lodThreshold * 0.95);

    const aspect = canvas.width / canvas.height;
    const vp = camera.viewProjection(aspect);
    const pos = camera.position();
    const dir = camera.direction();

    // Update tiles
    const { visibleCount, totalTris } = tileManager.update(
      vp,
      pos.x, pos.y, pos.z,
      dir.x, dir.y, dir.z,
      canvas.width, canvas.height,
      elevScale, lodThreshold,
    );

    // Render
    renderer.render(
      tileManager.iterActive(),
      vp,
      pos.x, pos.y, pos.z,
      elevScale,
    );

    // HUD
    const p = tileManager.perf;
    const detailZoom = Math.round(1 / DETAIL_SCALE);
    const effRes = 1024 * detailZoom;
    const detailMPerPx = Math.round(1085 / detailZoom);
    hud.textContent =
      `${fps.toFixed(0)} fps | ` +
      `${visibleCount} tiles | ` +
      `${(totalTris / 1000).toFixed(1)}k tris | ` +
      `${MAX_LOD1_TILES} max | ` +
      `detail: ${detailZoom}x ${effRes} @ ${detailMPerPx}m/px | budget: ${INFER_BUDGET} | ` +
      `elev: ${elevScale.toFixed(2)} | ` +
      `lod: ${lodThreshold.toFixed(1)}` +
      (tileManager.gpuCbtEnabled ? ' | GPU CBT' : '') +
      `\ncull ${p.cull.toFixed(1)} | cbt ${p.cbtUpdate.toFixed(1)} | mesh ${p.meshGen.toFixed(1)} | upload ${p.upload.toFixed(1)} | infer ${p.infer.toFixed(1)} | total ${p.total.toFixed(1)} ms` +
      `\n${(p.cbtLeaves / 1000).toFixed(1)}k leaves across ${p.cbtTilesUpdated.toFixed(0)} tiles`;

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);
}

function showError(msg: string): void {
  const el = document.getElementById('error')!;
  el.textContent = msg;
  el.style.display = 'block';
}

main().catch((e) => {
  console.error(e);
  showError(String(e));
});
